import {
    createBrowserRouter,
    redirect,
    type LoaderFunctionArgs,
    useParams
} from "react-router";

import {
    RouterProvider
} from "react-router/dom";

import {
    AppLayout
} from "./AppLayout.js";

import {
    appRoutePatterns
} from "./routes.js";

import {
    getSafeReturnTo as getSafeInternalReturnTo
} from "../core/returnTo.js";

import {
    LessonAccessBoundary
} from "../features/shop/LessonAccessBoundary.js";

import {
    AuthPage
} from "../pages/AuthPage.js";

import {
    ArchivePage
} from "../pages/ArchivePage.js";

import {
    AdminContentPage
} from "../pages/AdminContentPage.js";

import {
    GrammarIndexPage
} from "../pages/GrammarIndexPage.js";

import {
    GrammarLessonPage
} from "../pages/GrammarLessonPage.js";

import {
    GrammarLevelPage
} from "../pages/GrammarLevelPage.js";

import {
    HomePage
} from "../pages/HomePage.js";

import {
    JournalArticlePage
} from "../pages/JournalArticlePage.js";

import {
    JournalIndexPage
} from "../pages/JournalIndexPage.js";

import {
    NotFoundPage
} from "../pages/NotFoundPage.js";

import {
    OnboardingPage
} from "../pages/OnboardingPage.js";

import { PlacementPage } from "../pages/PlacementPage.js";

import {
    ProfilePage
} from "../pages/ProfilePage.js";

import {
    SaurusPage
} from "../pages/SaurusPage.js";

import {
    PracticeCatalogPage
} from "../pages/PracticeCatalogPage.js";

import {
    PracticeGamePage
} from "../pages/PracticeGamePage.js";

import {
    PracticeIndexPage
} from "../pages/PracticeIndexPage.js";

import {
    PracticeReviewPage
} from "../pages/PracticeReviewPage.js";

import {
    ShopPage
} from "../pages/ShopPage.js";

import {
    AboutPage
} from "../pages/AboutPage.js";

import {
    ContactPage
} from "../pages/ContactPage.js";

import {
    DinoAssistantPage
} from "../pages/DinoAssistantPage.js";

import {
    DailySessionPage
} from "../pages/DailySessionPage.js";

import {
    WorkWithUsPage
} from "../pages/WorkWithUsPage.js";

import {
    TravelIndexPage
} from "../pages/TravelIndexPage.js";

import {
    TravelLessonPage
} from "../pages/TravelLessonPage.js";

import {
    VocabularyIndexPage
} from "../pages/VocabularyIndexPage.js";

import {
    VocabularyLevelPage
} from "../pages/VocabularyLevelPage.js";

import {
    VocabularyPackPage
} from "../pages/VocabularyPackPage.js";

import {
    VocabularyReviewPage
} from "../pages/VocabularyReviewPage.js";

/**
 * Dino application router.
 *
 * Navigation now uses durable React routes instead of the historical custom
 * query-string router.
 *
 * Public route:
 *
 * /auth
 * /onboarding
 * /placement
 * /admin/content
 *
 * Application routes:
 *
 * /
 *
 * /grammar
 * /grammar/:level
 * /grammar/lesson/:lessonId
 *
 * /vocabulary
 * /vocabulary/:level
 * /vocabulary/:level/:packId
 *
 * /practice
 * /daily
 * /practice/review
 * /practice/:game/:level
 * /practice/:game/:level/:packId
 * /vocabulary/:level/:packId/review
 *
 * /travel
 * /travel/:lessonId
 *
 * /journal
 * /journal/:articleId
 *
 * /info/about
 * /info/contact
 * /info/dino
 * /info/work-with-us
 * /archive
 * /profile
 * /saurus
 * /shop
 */
const routerBasename =
    import.meta.env.BASE_URL;

const router =
    createBrowserRouter([
        {
            path:
                appRoutePatterns.auth,

            Component:
                AuthPage
        },

        {
            path:
                appRoutePatterns.onboarding,

            loader:
                onboardingLoader,

            Component:
                OnboardingPage
        },

        {
            path:
                appRoutePatterns.home,

            loader:
                requireCompletedOnboarding,

            Component:
                AppLayout,

            children: [
                {
                    index:
                        true,

                    Component:
                        HomePage
                },

                {
                    path:
                        appRoutePatterns.adminContent,

                    Component:
                        AdminContentPage
                },

                {
                    path:
                        appRoutePatterns.profile,

                    Component:
                        ProfilePage
                },

                {
                    path: appRoutePatterns.placement,
                    Component: PlacementPage
                },

                {
                    path:
                        appRoutePatterns.saurus,

                    Component:
                        SaurusPage
                },

                {
                    path:
                        appRoutePatterns.archive,

                    Component:
                        ArchivePage
                },

                {
                    path:
                        appRoutePatterns.shop,

                    Component:
                        ShopPage
                },

                /* ---------------------------------------------------------- */
                /* Games and exercises                                        */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.practiceIndex,

                    Component:
                        PracticeIndexPage
                },

                {
                    path:
                        appRoutePatterns.daily,

                    Component:
                        DailySessionPage
                },

                {
                    path:
                        appRoutePatterns.practiceCatalog,

                    Component:
                        PracticeCatalogPage
                },

                {
                    path:
                        appRoutePatterns.practiceReview,

                    Component:
                        PracticeReviewPage
                },

                {
                    path:
                        appRoutePatterns.practiceGame,

                    Component:
                        PurchasablePracticeGamePage
                },

                /* ---------------------------------------------------------- */
                /* Grammar                                                    */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.grammarIndex,

                    Component:
                        GrammarIndexPage
                },

                {
                    path:
                        appRoutePatterns.grammarLevel,

                    Component:
                        GrammarLevelPage
                },

                {
                    path:
                        appRoutePatterns.grammarLesson,

                    Component:
                        PurchasableGrammarLessonPage
                },

                /* ---------------------------------------------------------- */
                /* Vocabulary                                                 */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.vocabularyIndex,

                    Component:
                        VocabularyIndexPage
                },

                {
                    path:
                        appRoutePatterns.vocabularyLevel,

                    Component:
                        VocabularyLevelPage
                },

                {
                    path:
                        appRoutePatterns.vocabularyPack,

                    Component:
                        PurchasableVocabularyPackPage
                },

                {
                    path:
                        appRoutePatterns.vocabularyReview,

                    Component:
                        PurchasableVocabularyReviewPage
                },

                /* ---------------------------------------------------------- */
                /* Travel                                                     */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.travelIndex,

                    Component:
                        TravelIndexPage
                },

                {
                    path:
                        appRoutePatterns.travelLesson,

                    Component:
                        TravelLessonPage
                },

                /* ---------------------------------------------------------- */
                /* Journal                                                    */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.journalIndex,

                    Component:
                        JournalIndexPage
                },

                {
                    path:
                        appRoutePatterns.journalArticle,

                    Component:
                        JournalArticlePage
                },

                /* ---------------------------------------------------------- */
                /* Institutional                                              */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.about,

                    Component:
                        AboutPage
                },

                {
                    path:
                        appRoutePatterns.contact,

                    Component:
                        ContactPage
                },

                {
                    path:
                        appRoutePatterns.dino,

                    Component:
                        DinoAssistantPage
                },

                {
                    path:
                        appRoutePatterns.workWithUs,

                    Component:
                        WorkWithUsPage
                },

                /* ---------------------------------------------------------- */
                /* Fallback                                                   */
                /* ---------------------------------------------------------- */

                {
                    path:
                        appRoutePatterns.notFound,

                    Component:
                        NotFoundPage
                }
            ]
        }
    ], {
        basename:
            routerBasename
    });

/**
 * Protects the learning application until onboarding has enough persisted
 * information to operate.
 *
 * The complete requested location is stored in `returnTo`, allowing links such
 * as:
 *
 * /grammar/lesson/A1-G-001
 *
 * to continue to their original target after onboarding.
 */
function requireCompletedOnboarding({
    request
}: LoaderFunctionArgs) {
    if (
        hasCompletedOnboarding()
    ) {
        return null;
    }

    const url =
        new URL(
            request.url
        );

    const returnTo =
        getRouterRelativeLocation(
            url
        );

    return redirect(
        `/onboarding?returnTo=${encodeURIComponent(
            returnTo
        )}`
    );
}

/**
 * Removes the deployment basename from a browser URL before persisting an
 * onboarding return target. React Router adds the basename back when it
 * navigates, preventing duplicated `/Dino-5monde` segments on GitHub Pages.
 */
function getRouterRelativeLocation(
    url: URL
): string {
    const normalizedBasename =
        routerBasename === "/"
            ? ""
            : routerBasename.replace(
                /\/$/,
                ""
            );

    const pathname =
        normalizedBasename
        && (
            url.pathname === normalizedBasename
            || url.pathname.startsWith(
                `${normalizedBasename}/`
            )
        )
            ? url.pathname.slice(
                normalizedBasename.length
            ) || "/"
            : url.pathname;

    return `${pathname}${url.search}${url.hash}`;
}

/**
 * Prevents users with completed onboarding from being sent through the flow
 * again.
 */
function onboardingLoader({
    request
}: LoaderFunctionArgs) {
    if (
        !hasCompletedOnboarding()
    ) {
        return null;
    }

    const url =
        new URL(
            request.url
        );

    const returnTo =
        getSafeReturnTo(
            url.searchParams.get(
                "returnTo"
            )
        );

    return redirect(
        returnTo
    );
}

/**
 * Dino currently considers onboarding complete when:
 *
 * - a supported interface language is persisted
 * - a supported learning path is persisted
 *
 * Placement remains optional because the learner may explicitly choose to
 * postpone the test.
 */
function hasCompletedOnboarding():
    boolean {
    const language =
        localStorage.getItem(
            "language"
        );

    const path =
        localStorage.getItem(
            "currentPath"
        );

    const languageValid =
        language === "fr"
        || language === "fa";

    const pathValid =
        path === "general"
        || path === "travel";

    return (
        languageValid
        && pathValid
    );
}

/**
 * Sanitizes a post-onboarding destination.
 *
 * Only internal absolute application paths are accepted.
 *
 * This prevents:
 *
 * - external redirects
 * - protocol-relative `//example.com`
 * - malformed values
 * - redirect loops back to onboarding
 */
function getSafeReturnTo(
    value:
        string | null
): string {
    return getSafeInternalReturnTo(
        value,
        {
            blockedPaths: [
                "/onboarding"
            ]
        }
    );
}

function AppRouter() {
    return (
        <RouterProvider
            router={
                router
            }
        />
    );
}

function PurchasableGrammarLessonPage() {
    const {
        lessonId = ""
    } = useParams();

    return (
        <LessonAccessBoundary
            contentId={lessonId}
            contentType="grammar"
        >
            <GrammarLessonPage />
        </LessonAccessBoundary>
    );
}

function PurchasableVocabularyPackPage() {
    const {
        level,
        packId = ""
    } = useParams();

    return (
        <LessonAccessBoundary
            contentId={packId}
            contentType="vocabulary"
            level={level}
        >
            <VocabularyPackPage />
        </LessonAccessBoundary>
    );
}

function PurchasablePracticeGamePage() {
    const {
        level,
        packId = ""
    } = useParams();

    return (
        <LessonAccessBoundary
            contentId={packId}
            contentType="vocabulary"
            level={level}
        >
            <PracticeGamePage />
        </LessonAccessBoundary>
    );
}

function PurchasableVocabularyReviewPage() {
    const {
        level,
        packId = ""
    } = useParams();

    return (
        <LessonAccessBoundary
            contentId={packId}
            contentType="vocabulary"
            level={level}
        >
            <VocabularyReviewPage />
        </LessonAccessBoundary>
    );
}

export {
    AppRouter,
    getSafeReturnTo,
    hasCompletedOnboarding
};
